/* Photo capture: camera or library, compressed and stripped before it is stored.
 *
 * Two inputs rather than one, deliberately. `capture="environment"` opens the
 * camera straight away, which is what a technician standing at a locker wants;
 * a plain file input is what someone re-attaching a photo they already took (or
 * one a colleague sent them) needs, and on iOS the two are genuinely different
 * pickers. Forcing everything through one input costs a tap for one of them.
 *
 * The compressed size is printed under every thumbnail. That is not decoration:
 * it is the only way a technician can tell, before walking away, that one photo
 * is going to sit in the queue for an hour.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import './capture.css';
import {
  capturePhoto,
  describeCompression,
  domRasterPort,
  formatBytes,
  PhotoDecodeError,
  PhotoTooLargeError,
  type BlobSink,
  type CompressOptions,
  type RasterPort,
} from './photoPipeline';
import { MetadataNotStrippedError, NotJpegError } from './jpegMetadata';
import { putBlob, getBlob } from './blobStore';
import type { PhotoAttachment } from './types';

/** Everything the component needs from the outside world. Injectable for tests. */
export interface PhotoCaptureDeps {
  port: RasterPort;
  sink: BlobSink;
  /** Read bytes back out, for thumbnails. */
  loadBlob(blobId: string): Promise<Blob | undefined>;
  now(): number;
}

export interface PhotoCaptureProps {
  /** What this photo is of, e.g. `Serial plate, module 2`. Required — an
   *  unlabelled photo is unreviewable once it reaches Salesforce. */
  label: string;
  /** Photos already attached. This component is controlled. */
  value: readonly PhotoAttachment[];
  onChange: (photos: readonly PhotoAttachment[]) => void;
  /** Cap on how many photos this prompt takes. Defaults to 1. */
  max?: number;
  compress?: CompressOptions;
  /**
   * Warn above this compressed size. Defaults to 1 MB — twenty of those is a
   * 20 MB queue on a warehouse connection.
   */
  heavyBytes?: number;
  deps?: Partial<PhotoCaptureDeps>;
}

export function PhotoCapture({
  label,
  value,
  onChange,
  max = 1,
  compress,
  heavyBytes = 1024 * 1024,
  deps,
}: PhotoCaptureProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const libraryRef = useRef<HTMLInputElement | null>(null);

  // Memoised so the identity is stable across renders; an inline `{ putBlob }`
  // would rebuild `accept` on every keystroke elsewhere in the form.
  const port = deps?.port ?? null;
  const sink = useMemo<BlobSink>(() => deps?.sink ?? { putBlob }, [deps?.sink]);
  const loadBlob = useMemo(() => deps?.loadBlob ?? getBlob, [deps?.loadBlob]);
  const now = useMemo(() => deps?.now ?? (() => Date.now()), [deps?.now]);

  const accept = useCallback(
    async (file: File, source: 'camera' | 'library') => {
      setBusy(true);
      setError(null);
      try {
        const attachment = await capturePhoto(file, {
          // The DOM port is constructed lazily so a test that injects one never
          // touches `document`.
          port: port ?? domRasterPort(),
          sink,
          source,
          label,
          now,
          ...(compress ? { compress } : {}),
        });
        onChange([...value, attachment]);
      } catch (err) {
        setError(explain(err));
      } finally {
        setBusy(false);
      }
    },
    [port, sink, label, now, compress, onChange, value],
  );

  const remove = useCallback(
    (blobId: string) => {
      // The blob itself is left for the sync engine's housekeeping to collect:
      // deleting it here would lose the photo if the same id were still
      // referenced by an item already in flight.
      onChange(value.filter((p) => p.blobId !== blobId));
    },
    [onChange, value],
  );

  const full = value.length >= max;

  return (
    <section className="cap-sheet" aria-label={`Photo: ${label}`}>
      {/*
        A REAL HEADING, not a `<p>` that looks like one.
        `.section-title` gave this paragraph the size and weight of a heading and none of the
        semantics, so the one line naming what to photograph was invisible to the rotor / heading
        list — the primary way a screen-reader user skims a long form. `h3` rather than `h2`:
        `FormRenderer` renders the section title as `h2` and this sits inside one of its fields.
      */}
      <h3 className="section-title">{label}</h3>

      {value.length > 0 && (
        <ul className="cap-thumbs">
          {value.map((p) => (
            <Thumb key={p.blobId} photo={p} heavyBytes={heavyBytes} loadBlob={loadBlob} onRemove={remove} />
          ))}
        </ul>
      )}

      {error && (
        <p className="banner banner--danger" role="alert">
          {error}
        </p>
      )}

      <div className="cap-row">
        <button
          type="button"
          className="button"
          disabled={busy || full}
          onClick={() => cameraRef.current?.click()}
        >
          {busy ? 'Processing…' : 'Take photo'}
        </button>
        <button
          type="button"
          className="button"
          disabled={busy || full}
          onClick={() => libraryRef.current?.click()}
        >
          Choose from library
        </button>
      </div>

      {full && <p className="muted">{max === 1 ? 'One photo is enough here.' : `Limit of ${max} reached.`}</p>}

      {/*
        THE TWO FILE INPUTS ARE REMOVED FROM THE TAB ORDER AND FROM THE ACCESSIBILITY TREE.
        =================================================================================
        They exist only so the two visible buttons above can call `.click()` on them — the buttons
        are the control, these are the mechanism. `.cap-sr` hides them visually, which is not enough:
        a visually-hidden `<input type="file">` is still focusable and still announced, so a
        technician tabbing through this field hit two unnamed "Choose file" controls whose focus ring
        was clipped to nothing by `clip-path`, i.e. focus vanished. CSS cannot fix that — tab order
        and the accessibility tree are markup, not presentation.

        `tabIndex={-1}` + `aria-hidden` is safe here precisely because nothing is lost: every
        function these inputs offer is already reachable through a named, 60px, focusable button.
      */}
      <input
        ref={cameraRef}
        className="cap-sr"
        type="file"
        accept="image/*"
        capture="environment"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void accept(f, 'camera');
        }}
      />
      <input
        ref={libraryRef}
        className="cap-sr"
        type="file"
        accept="image/*"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void accept(f, 'library');
        }}
      />
    </section>
  );
}

function Thumb({
  photo,
  heavyBytes,
  loadBlob,
  onRemove,
}: {
  photo: PhotoAttachment;
  heavyBytes: number;
  loadBlob: (blobId: string) => Promise<Blob | undefined>;
  onRemove: (blobId: string) => void;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    let created: string | null = null;
    void loadBlob(photo.blobId).then((blob) => {
      if (!blob || revoked) return;
      created = URL.createObjectURL(blob);
      setUrl(created);
    });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [photo.blobId, loadBlob]);

  const heavy = photo.bytes > heavyBytes;

  return (
    <li className="cap-thumb">
      {url ? (
        <img src={url} alt={photo.label ?? 'Captured photo'} />
      ) : (
        <div style={{ height: 100, background: '#000' }} aria-hidden="true" />
      )}
      <div className={`cap-thumb__meta${heavy ? ' cap-thumb__meta--heavy' : ''}`}>
        <div>
          {formatBytes(photo.bytes)}
          {heavy ? ' — large' : ''}
        </div>
        <div>{describeCompression(photo)}</div>
        <div>
          {photo.width}×{photo.height}
        </div>
        <button
          type="button"
          className="button"
          onClick={() => onRemove(photo.blobId)}
          aria-label={`Remove photo: ${photo.label ?? photo.blobId}`}
        >
          Remove
        </button>
      </div>
    </li>
  );
}

/** Turn a pipeline failure into something a technician can act on. */
export function explain(err: unknown): string {
  if (err instanceof PhotoDecodeError) {
    return 'That file could not be read as a photo. Try taking it again.';
  }
  if (err instanceof PhotoTooLargeError) {
    return `${err.message}. Take the photo again from a little further back.`;
  }
  if (err instanceof MetadataNotStrippedError || err instanceof NotJpegError) {
    /*
     * Still refuses to queue the photo — the server re-encode (C-36) is the authoritative strip, and
     * shipping bytes we could not verify would make it the ONLY line of defence rather than the
     * second. That part is deliberate and is not being relaxed here.
     *
     * What IS new: the technical reason is now IN THE MESSAGE. The generic sentence alone sent us
     * guessing twice — first at EXIF, then at Chrome's APP2 ICC profile (a real fix, confirmed
     * present in the deployed bundle, and it did not resolve this). A refusal that cannot say what
     * it refused costs more than the ugliness of showing a marker name to a technician.
     *
     * `MetadataNotStrippedError.message` lists each offending segment with its marker name, length
     * and first printable bytes; `NotJpegError` says the output was not a JPEG at all. Either one
     * identifies the cause immediately.
     */
    // eslint-disable-next-line no-console -- the whole point is that this failure stops being silent.
    console.error('[lockercustody] photo refused by the sanitiser', err);
    return (
      'That photo could not be processed safely and was not saved. Please take it again. ' +
      `If it keeps happening, quote this: ${err.message}`
    );
  }
  return err instanceof Error ? err.message : 'That photo could not be saved.';
}
